// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { getCollectAreas } from 'apis/collects';
import CollectAreasMap from 'components/molecules/CollectAreasMap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getApiPath, mergeClasses, uniqRandomNumber } from 'util/commons';
import { CollectRenewError, PrintLabel } from 'util/Enums';
import { handleError } from 'util/errorHandler';
import { useQuery } from 'util/queryParams';
import { Area } from 'util/Types';
import styles from './CollectRouteMapView.module.scss';
import { CollectRouteSiteList } from 'types/routeManagementTypes';
import { useHistory, useParams } from 'react-router';
import { Path } from 'routes';

const ERR_MAX_COUNT = 5;
const RENEW_INTERVAL = 5000;

type CollectRouteMapViewProps = {
  currentRouteSites?: CollectRouteSiteList;
};

const CollectRouteMapView: React.FC<CollectRouteMapViewProps> = ({
  currentRouteSites,
}) => {
  const { t } = useTranslation();
  const queryParams = useQuery();
  const user = useSelector(selectUser);
  const keyword = queryParams.get('mapKeyword') || undefined;
  const { routeId } = useParams<{ routeId?: string }>();

  const [areas, setAreas] = useState<Area[]>([]);
  const [mapResetTrigger, setMapResetTrigger] = useState(uniqRandomNumber());
  const [renewDataError, setRenewDataError] = useState<CollectRenewError>();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errCounter = useRef(0);
  const isFirstLoad = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const history = useHistory();

  const callApi = async (params?: {
    showLoading?: boolean;
    showError?: boolean;
    resetMap?: boolean;
    successCallback?: () => void;
    failedCallback?: (err: any) => void;
    finallyCallback?: () => void;
    signal?: AbortSignal;
  }) => {
    if (currentRouteSites && !currentRouteSites.sites.length) {
      return;
    }

    const {
      showLoading = true,
      showError = true,
      resetMap = true,
      successCallback,
      failedCallback,
      finallyCallback,
      signal,
    } = params || {};

    await getCollectAreas({
      keyword,
      routeId,
      successCallback: (data) => {
        setAreas(data);
        successCallback?.();

        if (resetMap) {
          setTimeout(() => {
            setMapResetTrigger(uniqRandomNumber());
          });
        }
      },
      axiosConfigs: {
        prefix: 'v2',
        notShowLoading: !showLoading,
        signal,
      },
      showError,
      finallyCallback,
      failedCallback,
    });
  };

  useEffect(() => {
    if (currentRouteSites && !currentRouteSites.sites.length) {
      setAreas([]);
      setRenewDataError(undefined);

      return;
    }

    isFirstLoad.current = true;
    errCounter.current = 0;
    setRenewDataError(undefined);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const handleCallApi = () =>
      callApi({
        showError: isFirstLoad.current,
        showLoading: isFirstLoad.current,
        resetMap: isFirstLoad.current,
        signal: abortController.signal,
        successCallback: () => {
          errCounter.current = 0;
          setRenewDataError(undefined);
        },
        failedCallback: (e) => {
          if (e.name === 'AbortError' || abortController.signal.aborted) {
            return;
          }

          errCounter.current += 1;
          setRenewDataError(CollectRenewError.RENEW_FAILED);

          if (errCounter.current >= ERR_MAX_COUNT) {
            handleError(e);
          }
        },
        finallyCallback: () => {
          if (abortController.signal.aborted) {
            return;
          }

          isFirstLoad.current = false;

          if (errCounter.current < ERR_MAX_COUNT) {
            timeoutRef.current = setTimeout(() => {
              if (!abortController.signal.aborted) {
                handleCallApi();
              }
            }, RENEW_INTERVAL);
          } else {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }

            setRenewDataError(CollectRenewError.MAX_RETRY_EXCEEDED);
          }
        },
      });

    handleCallApi();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [keyword, user.role, currentRouteSites]);

  const renderCollectButton = (area: Area) => {
    const handleGoToCollectPage = () => {
      let url = getApiPath(Path.collectItemInSite, {
        routeId: routeId ?? 'none',
        siteId: area.siteId,
      });

      if (area.enableSubstituteRegist) {
        url = getApiPath(Path.substituteRegistration, {
          routeId: routeId ?? 'none',
          siteId: area.siteId,
        });
      } else if (area.sitePrintLabel === PrintLabel.QRCode) {
        url = getApiPath(Path.routeSiteQRScanning, {
          routeId: routeId ?? 'none',
          siteId: area.siteId,
        });
      }

      history.push(`${url}${window.location.search}`); // keep map state in query params
    };

    // All buttons will use onClick to navigate to keep map state, please check CollectAreasMap for more details
    if (area.enableSubstituteRegist) {
      return (
        <a
          className={mergeClasses(styles.collectAreaButton, {
            [styles.buttonGray]: area.substituteRegistRelations.some(
              ({ registFlg }) => registFlg
            ),
          })}
          onClick={handleGoToCollectPage}
        >
          {t('collects.button_to_substitute_regist')}
        </a>
      );
    }

    if (area.sitePrintLabel === PrintLabel.QRCode) {
      return (
        <a
          className={mergeClasses(styles.collectAreaButton, {
            [styles.buttonGray]: !area.emissionFlg && !area.collectRequestFlg,
          })}
          onClick={handleGoToCollectPage}
        >
          {t('collects.button_to_scan_qr_code')}
        </a>
      );
    }

    return (
      <a
        className={mergeClasses(styles.collectAreaButton, {
          [styles.buttonGray]: !area.emissionFlg && !area.collectRequestFlg,
        })}
        onClick={handleGoToCollectPage}
      >
        {t('collects.button_to_recover')}
      </a>
    );
  };

  const sortAreas = (areas: Area[]) =>
    areas.sort((a, b) => {
      if (a.emissionFlg !== b.emissionFlg) {
        return b.emissionFlg ? 1 : -1;
      }

      return a.areaName.localeCompare(b.areaName);
    });

  const sortedAreas = useMemo(() => sortAreas(areas), [areas]);

  if (sortedAreas.length === 0) {
    return (
      <div className={styles.emptyList}>
        <p>{t('messages.M_008')}</p>
      </div>
    );
  }

  return (
    <>
      <CollectAreasMap
        data={sortedAreas}
        renderCollectButton={renderCollectButton}
        resetTrigger={mapResetTrigger}
        isShowingInDetailPage={!!currentRouteSites}
        isShowingSearchResult={!!keyword}
      />

      {renewDataError && (
        <div
          className={mergeClasses(styles.collectErrorMessage, {
            [styles.collectAlertMessage]:
              renewDataError === CollectRenewError.RENEW_FAILED,
          })}
        >
          {t(`collect_map.${renewDataError}`)}
        </div>
      )}
    </>
  );
};

export default CollectRouteMapView;
